import { getIsProdEnv, isDebug, WorkerPayloadTypes } from "@/constants";
import { Context as DiceWorkerContext } from "@/context";
import { getWorkerName } from "@/helpers/getWorkerName";
import type { User } from "@dndbeyond/auth/types/user";
import { useEffect, useState, type FC, type PropsWithChildren } from "react";

const getWorker = async (nodeEnv: string, env: string, user?: User) => {
  if (process.env.NODE_ENV === "development") {
    const { getDevWorker } = await import("./getDevWorker");

    return getDevWorker(nodeEnv, env, user);
  }

  return new Worker(
    `/dice/${getIsProdEnv(env) ? "mobile" : "previewer"}/worker.${process.env.NEXT_PUBLIC_WORKER_HASH}.js`,
    {
      name: getWorkerName(nodeEnv, env, user),
      type: "module",
    },
  );
};

interface ContextProviderProps extends PropsWithChildren {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- logger type needs to be properly decoupled from feywild
  logger: any;
  errorCallback?: (message: string) => void;
  nodeEnv: string;
  env: string;
  user?: User;
  isFullNameEqual?: boolean;
}

export const ContextProvider: FC<ContextProviderProps> = ({
  children,
  logger,
  errorCallback,
  nodeEnv,
  env,
  user,
  isFullNameEqual,
}) => {
  const [worker, setWorker] = useState<Worker>();
  const [hasWorkerErrored, setHasWorkerErrored] = useState(false);
  const [isWorkerReady, setIsWorkerReady] = useState(false);

  useEffect(() => {
    if (!user || Boolean(worker)) return;

    (async () => {
      const newWorker = await getWorker(nodeEnv, env, user);
      const handleMessage = ({ data: { type } }: MessageEvent) => {
        if (type === WorkerPayloadTypes.ComponentMounted) {
          newWorker.removeEventListener("message", handleMessage);
          setIsWorkerReady(true);
        }
      };

      newWorker.addEventListener("message", handleMessage);
      newWorker.onerror = (error) => {
        // clean up message handler if error happens before component mounts
        newWorker.removeEventListener("message", handleMessage);

        if (isDebug) {
          logger.error("dice worker error", {
            exception: JSON.stringify(error.message),
          });
        }

        newWorker.postMessage({
          type: WorkerPayloadTypes.RemoveEventListener,
        });
        errorCallback?.(
          "Dice worker encountered an error. Falling back to instant dice.",
        );
        setHasWorkerErrored(true);
      };

      if (isDebug) {
        newWorker.addEventListener("message", (event) => {
          if (event.data.type === "debug") {
            // replace logger.log with alert() for mobile debugging
            logger.log(event.data.payload);
          }
        });
      }

      setWorker(newWorker);
    })();

    return () => {
      worker?.terminate();
    };
  }, [env, errorCallback, isFullNameEqual, logger, nodeEnv, user, worker]);

  return user ? (
    <DiceWorkerContext.Provider
      value={{
        worker,
        nodeEnv,
        env,
        user,
        hasWorkerErrored,
        isWorkerReady,
      }}
    >
      {children}
    </DiceWorkerContext.Provider>
  ) : (
    children
  );
};
